import { gql, useApolloClient, useLazyQuery, useQuery } from "@apollo/client";
import { getFetchPolicyOptions } from "api/utils";
import { ContactOverviewQuery } from "../types";

export const CONTACT_OVERVIEW_QUERY = gql`
  query GetPortfolioOverview($contactId: Long) {
    contact(id: $contactId) {
      id
      contactId
      name
      addresses {
        id
        address1
        address2
        zipCode
        city
        phone1
        email
      }
      classification {
        id
        code
        name
        namesAsMap
      }
      primaryAddress {
        id
        address1
        address2
        zipCode
        city
        phone1
        email
      }
      juridical {
        id
        code
        name
      }
      representatives {
        id
        contactId
        name
      }
      taxCountry {
        id
        code
        name
      }
      nationality {
        id
        code
        name
      }
      extContactIdList: extContactIds {
        id
        extId
        extName
      }
      portfolios {
        id
        name
        shortName
        profile {
          ipsWdAge: attribute(
            attributeKey: "portfolio.noTaxReporting.ipsWdAge"
          ) {
            value: intValue
          }
          ipsSavingPlan: attribute(
            attributeKey: "portfolio.noTaxReporting.ipsSavingPlan"
          ) {
            value: intValue
          }
        }
        extPortfolioIds {
          id
          name
          extId
        }
        fees {
          id
          type
          feePercent
        }
        status
        typeCode
        modelPortfolio {
          id
          name
          code
          linkedPortfolio {
            id
            shortName
            name
          }
        }
        accounts {
          id
          number
          tagsAsSet
          name
        }
        tagsAsSet
        fees {
          id
          type
          feePercent
        }
        portfolioReport {
          marketValue
          accountBalance
          positionMarketValue
          accountItems {
            balanceUnitPrice
            balanceAccCurr
            accountId
            accountName
            balance
            currency {
              id
              securityCode
            }
          }
          portfolioReportItems {
            amount
            shareOfPortfolio
            marketValue
            portfolioId
            purchaseTradeAmount
            marketUnitPrice
            accruedInterest
            security {
              securityCode
              isinCode
              classType1 {
                id
                code
                value
                valuesAsMap
              }
              classType2 {
                id
                code
                value
                valuesAsMap
              }
              id
              name
              managementFeePercentage
              kickBackPercentage
              currency {
                id
                securityCode
              }
            }
          }
        }
      }
    }
  }
`;

export const useGetContactOverview = (
  contactId: string | number | undefined,
  avoidSkip = false
) => {
  const { loading, error, data, refetch, networkStatus } =
    useQuery<ContactOverviewQuery>(CONTACT_OVERVIEW_QUERY, {
      variables: { contactId },
      skip: !contactId && !avoidSkip,
      notifyOnNetworkStatusChange: true,
      ...getFetchPolicyOptions(`useGetContactOverview.${contactId}`),
    });

  return {
    loading: loading,
    error: error,
    data: data,
    refetch,
    networkStatus,
  };
};

export const useLazyGetContactOverview = () => {
  const client = useApolloClient();

  const getContactOverview = async (contactId: string | number | undefined) => {
    const { data, networkStatus, loading, error } =
      await client.query<ContactOverviewQuery>({
        query: CONTACT_OVERVIEW_QUERY,
        variables: { contactId },
        fetchPolicy: "cache-first",
      });
    return { data, networkStatus, loading, error };
  };

  return {
    getContactOverview,
  };
};
